import type { QuestionFilters } from './question-filters'
import type { QuestionLearner, QuestionStat } from '~/gql/questions/questions'
import { useLazyQuery } from '@apollo/client/react'
import { Download, FileText, Loader } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { GQL_MODULE_QUESTION_ANSWERS } from '~/gql/questions/questions'
import { downloadFromApi } from '~/helpers/files/download-from-api'
import { tourAnchor } from '~/helpers/tour/anchors'
import { downloadCSV } from '~/utils/exportCsv/utils'
import { answersCsv, questionCsv } from './question-csv'
import { questionsInput } from './question-filters'

type Props = {
  filters: QuestionFilters
  /** Rien à exporter : pas de module choisi, ou aucune réponse. */
  disabled: boolean
  pathName: string
  moduleTitle: string
}

/**
 * Les exports de l'analyse (#159), sur les filtres de l'écran. Les CSV lisent
 * la réponse de chaque apprenant au moment de l'export, que l'écran ne charge pas.
 */
function QuestionsExports({ filters, disabled, pathName, moduleTitle }: Props) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [loadAnswers] = useLazyQuery(GQL_MODULE_QUESTION_ANSWERS, { fetchPolicy: 'network-only' })

  const run = async (action: () => Promise<void>, errorKey: string) => {
    setBusy(true)
    try {
      await action()
    } catch {
      toast.error(t(errorKey))
    } finally {
      setBusy(false)
    }
  }

  const exportCsv = (build: (questions: QuestionStat[], learners: QuestionLearner[]) => { headers: string[], rows: (string | number)[][] }, fileKey: string) =>
    run(async () => {
      const { data } = await loadAnswers({ variables: { input: questionsInput(filters) } })
      if (!data) throw new Error('no answers')
      const { headers, rows } = build(data.moduleQuestionStats.questions as QuestionStat[], data.moduleQuestionStats.learners as QuestionLearner[])
      downloadCSV(t(fileKey), headers, rows)
    }, 'questions.export.failed')

  const exportPdf = () => run(async () => {
    const query = new URLSearchParams(Object.entries(questionsInput(filters)) as [string, string][])
    await downloadFromApi(`/statistics/questions-report?${query}`, 'analyse-questions.pdf')
  }, 'questions.export.pdfFailed')

  return (
    <div className="flex flex-wrap items-center gap-2" {...tourAnchor('questions.exports')}>
      <Button variant="outline" disabled={disabled || busy} onClick={() => void exportCsv((q, l) => questionCsv(q, l, { pathName, moduleTitle }, t), 'questions.export.questionsFile')}>
        <Download className="mr-2 size-4" />
        {t('questions.export.questions')}
      </Button>
      <Button variant="outline" disabled={disabled || busy} onClick={() => void exportCsv((q, l) => answersCsv(q, l, t), 'questions.export.answersFile')}>
        <Download className="mr-2 size-4" />
        {t('questions.export.answers')}
      </Button>
      <Button variant="outline" disabled={disabled || busy} onClick={() => void exportPdf()}>
        {busy ? <Loader className="mr-2 size-4 animate-spin" /> : <FileText className="mr-2 size-4" />}
        {t('questions.export.pdf')}
      </Button>
    </div>
  )
}

export default QuestionsExports
